/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { Button, Segment } from "semantic-ui-react";
import { TextField } from "@mui/material";
import { selectCurrentUserApiToken, selectCurrentUserName } from "../slices/currentUser/currentUserSlice";
import { changeBrowserTabTitle } from "../slices/browserTabTitle/browserTabTitleSlice";
import { changeTool } from "../slices/footer/footerSlice";
import { selectSearchSettings } from "../slices/searchSettings/searchSettingsSlice";
import submitPmid, { getPmidTitleListFromMedlineSearch } from "../functions/submitPmidAPI";
import submitFhirJson from "../functions/submitFhirJsonAPI";
import AlertModal from "./AlertModal";
import SaveSearchModal from "./SaveSearchModal";
import { DisplayFromFHIRReference } from "../datatypeComponents/ReferenceEntry";
import { changeFoiFocus } from "../slices/foiOfFocus/foiOfFocusSlice";

// eslint-disable-next-line no-empty-pattern
const MedlineConvertPage = ({ }) => {

    const dispatch = useDispatch();

    const navigate = useNavigate();
    const apiToken = useSelector(selectCurrentUserApiToken);
    const userName = useSelector(selectCurrentUserName);
    const searchSettings = useSelector(selectSearchSettings);

    const [medlineEntryState, setMedlineEntryState] = useState({
        "pmid": "", "submitButtonText": "Submit", "status": "active", "loadingSubmission": false, "runSavedSearch": false
    });
    const [alertModalState, setAlertModalState] = useState({
        open: false, alertHistory: [], header: "", content: ""
    });
    const [openSaveSearchModalState, setOpenSaveSearchModalState] = useState(false);
    const [searchResultsLibraryState, setSearchResultsLibraryState] = useState({});
    const [saveSearchResultsClickedState, setSaveSearchResultsClickedState] = useState(false);

    const changeMedlineEntryState = (newValue, field) => {
        setMedlineEntryState(prevState => {
            return {
                ...prevState,
                [field]: newValue
            };
        });
    };

    const submitPMID = async (pmid, multiple, redirect) => {
        pmid = pmid ?? medlineEntryState.pmid;
        multiple = multiple ?? false;
        redirect = redirect ?? true;

        pmid = pmid.replaceAll(" ", "");
        if (pmid.match(/[^$\d]/)) {
            setAlertModalState(prevState => {
                return {
                    ...prevState,
                    "open": true,
                    "header": "Error",
                    "content": "The PMID cannot contain any non-numerical characters: " + pmid
                };
            });
        } else {
            pmid = parseInt(pmid).toString();
            let response = await submitPmid(pmid, multiple, medlineEntryState.status, apiToken);
            if (response?.success) {
                if (redirect) {
                    //dispatch(changeFoiFocus(response.formstateid.toString()));
                    navigate(`/resources/Citation/${response.formstateid}`);
                } else {
                    return response;
                }
            } else if (response?.errormessage) {
                setAlertModalState(prevState => {
                    return {
                        ...prevState,
                        "open": true,
                        "header": "Error",
                        "content": response.errormessage
                    };
                });
                return response;
            } else {
                setAlertModalState(prevState => {
                    return {
                        ...prevState,
                        "open": true,
                        "header": "Error",
                        "content": "We were unable to convert this citation: " + pmid + ". Perhaps this PMID entry doesn't have Medline XML. Maybe it's a Book entry, and we're only converting Journal Articles at this time."
                    };
                });
            }
        }
    };

    const submitLibraryResource = async (fhirJson, title) => {
        let response = await submitFhirJson(fhirJson, title, apiToken);
        let resourceFOI;
        if (response?.success) {
            resourceFOI = response.formstateid;
        } else {
            setAlertModalState(prevState => {
                return {
                    ...prevState,
                    "open": true,
                    "header": "Error",
                    "content": "Problem creating Library of resulting Citation Resources."
                };
            });
            return null;
        }
        dispatch(changeFoiFocus(resourceFOI));
        navigate(`/resources/Library/${resourceFOI}`);
    }

    const submitMedlineQuery = async () => {
        if (medlineEntryState.pmid) {
            changeMedlineEntryState(true, "loadingSubmission");
            let response = await getPmidTitleListFromMedlineSearch(medlineEntryState.pmid, apiToken);
            changeMedlineEntryState(false, "loadingSubmission");
            let title = "MEDLINE Search Results for " + medlineEntryState.pmid.substring(0, 40) + " for " + userName;
            let fhirJson = {
                "resourceType": "Library", "title": title, "status": "active",
                "type": {
                    "coding": [{
                        "system": "http://terminology.hl7.org/CodeSystem/library-type",
                        "code": "asset-collection", "display": "Asset Collection"
                    }]
                },
                "date": new Date().toISOString(),
                "description": "",
                "content": []
            };

            if (response?.success) {
                if (response.pmidList?.length > 0 && response.pmidDictionary) {
                    fhirJson.description = "Your search includes " + response.pmidList.length + " Citations.";
                    if (response.pmidList.length >= 9999) {
                        fhirJson.description += " (Search results are not included after 10,000 entries.)";
                    }
                    for (const pmid in response.pmidDictionary) {
                        fhirJson.content.push({
                            "url": "https://pubmed.ncbi.nlm.nih.gov/" + pmid,
                            "title": response.pmidDictionary[pmid]
                        })
                    }
                } else {
                    fhirJson.description = "No Citations were found on your search.";
                }
                fhirJson.description += " Your search string was: " + medlineEntryState.pmid;
                if (response.noResults) {
                    setAlertModalState(prevState => {
                        return {
                            ...prevState,
                            "open": true,
                            "header": "",
                            "content": "No results found for this query."
                        };
                    });
                } else {
                    return fhirJson;
                }
            } else {
                setAlertModalState(prevState => {
                    return {
                        ...prevState,
                        "open": true,
                        "header": "Error",
                        "content": "Something went wrong with the search query."
                    };
                });
            }
        } else {
            setAlertModalState(prevState => {
                return {
                    ...prevState,
                    "open": true,
                    "header": "",
                    "content": "Please enter a PMID Query."
                };
            });
        }
    }

    const handleKeyUp = (event) => {
        if (event.key === 'Enter' && !alertModalState.open) {
            submitPMIDEntry();
        }
    }

    const submitPMIDEntry = async () => {
        if (medlineEntryState.pmid) {
            changeMedlineEntryState(true, "loadingSubmission");
            if (medlineEntryState.pmid.match(/[^$\d\s,]/)) {
                let libraryResource = await submitMedlineQuery();
                if (libraryResource?.resourceType === "Library") {
                    setSearchResultsLibraryState(libraryResource);
                }
            } else if (medlineEntryState.pmid.includes(",")) {
                let libraryResource = await submitMedlineQuery();
                if (libraryResource?.resourceType === "Library") {
                    setSearchResultsLibraryState(libraryResource);
                }
            } else {
                await submitPMID();
            }
            changeMedlineEntryState(false, "loadingSubmission");
        } else {
            setAlertModalState(prevState => {
                return {
                    ...prevState,
                    "open": true,
                    "header": "",
                    "content": "Please enter a PMID, a comma-separated list of PMIDs, or a search query."
                };
            });
        }
    }

    useEffect(() => {
        dispatch(changeBrowserTabTitle("FEvIR MEDLINE-to-FEvIR Converter"));
        dispatch(changeTool("Computable Publishing®: MEDLINE-to-FEvIR Converter"));
    }, []);

    useEffect((() => {
        if (!medlineEntryState.pmid) {
            changeMedlineEntryState("Submit", "submitButtonText");
        } else {
            if (medlineEntryState.pmid.match(/[^$\d\s,]/)) {
                changeMedlineEntryState("Submit Search Query", "submitButtonText");
            } else if (medlineEntryState.pmid.includes(",")) {
                changeMedlineEntryState("Submit PMID List", "submitButtonText");
            } else {
                changeMedlineEntryState("Submit PMID", "submitButtonText");
            }
        }
    }), [medlineEntryState.pmid]);

    useEffect(() => {
        if (searchSettings.source === "medline") {
            let searchQueryString = searchSettings.searchstring;
            if (searchSettings.searchparameters?.updatedSearchStartDate) {
                let startdate = searchSettings.searchparameters.updatedSearchStartDate || "1500";
                searchQueryString += ` AND (${startdate}:3000[Date - Modification] OR ${startdate}:3000[dp])`;
            }
            setMedlineEntryState({
                "runSavedSearch": true,
                "pmid": searchQueryString
            });
        }
    }, [searchSettings]);

    useEffect(() => {
        if (medlineEntryState.runSavedSearch) {
            submitPMIDEntry();
        }
    }, [medlineEntryState.runSavedSearch]);

    useEffect(() => {
        if (saveSearchResultsClickedState === true &&
            searchResultsLibraryState?.resourceType === "Library") {
            submitLibraryResource(searchResultsLibraryState, searchResultsLibraryState.title);
        }
    }, [saveSearchResultsClickedState]);

    return (
        <div>
            <AlertModal alertModalState={alertModalState} setAlertModalState={setAlertModalState} />
            <SaveSearchModal openSaveSearchModalState={openSaveSearchModalState}
                setOpenSaveSearchModalState={setOpenSaveSearchModalState}
                source={"medline"} searchString={medlineEntryState.pmid}
                header={"Label your Medline search"}
                searched={searchResultsLibraryState?.resourceType === "Library"} />
            <Segment className={`containerSegment maxRemainderOfPageSegment`} raised>
                <br />
                {apiToken ?
                    <>
                        <TextField style={{ width: "480px" }} className="inputField"
                            size="small" variant='outlined'
                            placeholder='PMID, PMIDs List, or Search Query' //type='number' inputMode='numeric'
                            value={medlineEntryState.pmid} onKeyUp={handleKeyUp}
                            onChange={(e) => { changeMedlineEntryState(e.target.value, "pmid"); }}
                        />
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                        <span style={{display: 'inline-block', marginTop: "5px"}}>
                            <Button className="formButton positive"
                                content={medlineEntryState.submitButtonText}
                                compact
                                onClick={submitPMIDEntry} />
                            &nbsp;&nbsp;
                            {medlineEntryState.submitButtonText === "Submit Search Query" &&
                                <Button className="formButton positive" content={"Save Search"} compact
                                    onClick={() => { setOpenSaveSearchModalState(true); }}
                                />}
                        </span>
                        <br /><br />
                        {medlineEntryState.loadingSubmission &&
                            <img className={medlineEntryState.loadingSubmission ? 'visible' : 'invisible'}
                                style={{ height: "22px" }} src="/spinner.gif"
                                alt="Loading" />}
                        <div style={{ "textAlign": "left", "width": "100%" }}>
                            <p>Enter a PMID to automatically create a HL7<sup>&#174;</sup> FHIR<sup>&#174;</sup> Citation Resource (or enter multiple PMIDs separated by commas).</p>
                            <p>HL7<sup>&#174;</sup>, and FHIR<sup>&#174;</sup> are the registered trademarks of Health Level Seven International and their use of these trademarks does not constitute an endorsement by HL7.</p>
                        </div>
                        <br /><br /><br />
                        {searchResultsLibraryState?.resourceType === "Library" &&
                            <div>
                                <Button className="formButton positive" content={"Save Search Results"} compact
                                    onClick={() => { setSaveSearchResultsClickedState(true); }}
                                />
                                <br /><br />
                                <p>{searchResultsLibraryState.description}</p>
                                <>{searchResultsLibraryState.relatedArtifact?.length > 0 &&
                                    searchResultsLibraryState.relatedArtifact.map((item, itemIndex) => {
                                        if (item.type === "contains") {
                                            return <div key={itemIndex}>
                                                <span>{(itemIndex + 1)}.</span>
                                                <DisplayFromFHIRReference reference={item.resourceReference} />
                                                <br />
                                            </div>
                                        } else {
                                            return <p>{(itemIndex + 1)}. Technical artifact.</p>
                                        }
                                    })}</>
                                <>{searchResultsLibraryState.content?.length > 0 &&
                                    searchResultsLibraryState.content.map((item, itemIndex) => {
                                        let pmid = item.url?.replace("https://pubmed.ncbi.nlm.nih.gov/", "");
                                        if (pmid && !isNaN(pmid)) {
                                            return <div key={pmid}>
                                                <span>{(itemIndex + 1)}. </span>
                                                <Link to="/" className="disabledCursor"
                                                    onClick={(event) => {
                                                        event.preventDefault();
                                                        submitPMID(pmid);
                                                    }}>
                                                    {item.title} (PMID {pmid})
                                                </Link>
                                                <br />
                                            </div>
                                        } else {
                                            return <p>{(itemIndex + 1)}. ERROR OCCURRED.</p>
                                        }
                                    })}</>
                            </div>}
                    </>
                    :
                    <p>Please login. (If you do not have an account click Sign In and log in with your Google Account.)</p>
                }
            </Segment>
        </div>
    );
};

export default MedlineConvertPage;